import React, { FC, useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import MaterialReactTable, { MRT_ColumnDef } from "material-react-table";
import { formatDateTimeWithSeconds } from '../../utils/DateTimeUtils';
import { UserType } from "../../enums/common";
import CallButton from "../Common/CallButton/CallButton";
import PartnerSlotService from "../../services/PartnerTimeSlotService";
import ShowSlotsDialog from "../Common/ShowSlotsDialog";
import AssignCookOnChefForParty from "../Common/AssignCookOnChefForParty";
import AddPartyAllocationComment from "../Common/AddPartyAllocationComment";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import ChefForPartyService from "../../services/ChefForPartyService";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import { FreshLeadStatus } from "../../enums/FreshLeadStatus";
import { AllCookOnBoardingSubStatus } from "../../enums/AllCookOnboardingSubStatus";
import { CookWorkStatus } from "../../enums/CookWorkStatus";
import { servesParty } from "../../enums/CookServiceTypeEnum";
import { ChefForPartyBookingTypeEnum } from "../../enums/ChefForPartyBookingTypeEnum";
import CityService from "../../services/CityService";
import type {
  ColumnFiltersState,
  SortingState,
  PaginationState,
  VisibilityState,
} from '@tanstack/react-table';
import AddPartyQuotedPrice from "../Common/UpdatedQuotedPriceDialog";


const cityService = new CityService();
const partnerSlotService = new PartnerSlotService();
const chefForPartyService = new ChefForPartyService();

const CookOnBoardingTypes = Object.values(FreshLeadStatus).map((x) => ({
  text: x,
  value: x,
}));

const CookOnBoardingSubTypes = Object.values(AllCookOnBoardingSubStatus).map(
  (x) => ({
    text: x,
    value: x,
  })
);

const CookWorkStatusMap = Object.values(CookWorkStatus).map((x) => ({
  text: x,
  value: x,
}));

// Party cuisines map: cook profile rating field -> display label.
// A cook is considered to "know" a cuisine when its final rating is above the threshold.
const PARTY_CUISINES: { param: string; label: string }[] = [
  { param: "northIndianFinalRating", label: "North Indian" },
  { param: "southIndianFinalRating", label: "South Indian" },
  { param: "chineseDishFinalRating", label: "Chinese" },
  { param: "continentalDishFinalRating", label: "Continental" },
  { param: "mexicanDishFinalRating", label: "Mexican" },
  { param: "thaiDishFinalRating", label: "Thai" },
];
const CUISINE_SCORE_THRESHOLD = 50;

const cuisineLabelForParam = (param: string) =>
  PARTY_CUISINES.find((c) => c.param === param)?.label ?? param;

const cuisineScore = (cook: any, param: string): number =>
  Number(cook?.[param]) || 0;

// Cuisines a cook knows = those whose per-cuisine rating is above the threshold.
const getKnownCuisines = (cook: any) =>
  PARTY_CUISINES.filter((c) => cuisineScore(cook, c.param) > CUISINE_SCORE_THRESHOLD);

// Compact tighter cell styling so more columns fit on one screen.
const headCellSx = { sx: { px: 0.75, py: 0.5, fontSize: 11, verticalAlign: "top" } };
const bodyCellSx = { sx: { px: 0.75, py: 0.5, fontSize: 12 } };
const compactBtnSx = { fontSize: 11, py: 0.25, px: 1, minWidth: 0, lineHeight: 1.3 };

// The allocation list is near-identical for every party of a date, so a comment left on one party
// ("not available today") is really about the cook's day and is merged into every party of that
// date. `currentPartyId` marks the ones that came from a different party so the team can tell them
// apart instead of calling the cook again. Chef Notes reuse this table and pass no party.
const AllocationNotes = ({ notes, headerAction, currentPartyId }: any) => (
  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
    {headerAction && (
      <Box display="flex" justifyContent="flex-end" p={0.5}>
        {headerAction}
      </Box>
    )}
    <Table size="small" sx={{ border: "1px solid #e0e0e0" }}>
      <TableHead>
        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
          <TableCell sx={{ fontWeight: 600 }}>Comment</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Commenter Email</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {notes.map((note: any, idx: number) => (
          <TableRow key={idx}>
            <TableCell>
              {note.comment || "Not Available"}
              {currentPartyId && note.partyId && note.partyId !== currentPartyId && (
                <Chip
                  size="small"
                  label={`Party #${note.partyId}`}
                  title="Added on another party of the same date"
                  sx={{ ml: 0.5, height: 16, fontSize: 10 }}
                />
              )}
            </TableCell>
            <TableCell>{note.commenterEmail || "Not Available"}</TableCell>
            <TableCell>
              {note.timestamp
                ? formatDateTimeWithSeconds(note.timestamp)
                : "Not Available"}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

interface PartyAllocationMetaShowProps {
  data: any[];
  trail: any;
  reload: () => void;
  blockedCooks?: any[];
  preferredCooks?: any[];
}

// A cook is eligible for party allocation only if its cookTypeList carries a party shift.
// Any of the three counts — the backend matches the whole party family, so a half-day party
// cook is as eligible as a full-day one.
const isPartyChef = (cook: any) => servesParty(cook?.cookTypeList);

const PartyAllocationMetaShow: FC<PartyAllocationMetaShowProps> = ({
  data,
  trail,
  reload,
  blockedCooks = [],
  preferredCooks = [],
}) => {
  const [showSlots, setShowSlots] = useState(false);
  const [slotData, setSlotData] = useState<any[]>([]);
  const [openAssignCook, setOpenAssignCook] = useState(false);
  const [assignCookData, setAssignCookData] = useState<any>(null);
  const [openAddComment, setOpenAddComment] = useState(false);
  const [commentData, setCommentData] = useState<any>(null);
  const [cities, setCities] = useState<any[]>([]);
  const [openPrice, setOpenPrice] = useState(false);
  const [priceData, setPriceData] = useState<any>(null);

  // ⭐ Stable reload to prevent useMemo warnings
  const stableReload = useCallback(() => {
    reload();
  }, [reload]);

  // ⭐ Fetch cities once
  useEffect(() => {
    cityService
      .fetchAllCities(1000, 0, { isCurrentlyServing: "YES" }, {})
      .then((res) => setCities(res?.data));
  }, []);

  // Distinct cuisines required by the party's selected dishes (param + label).
  // Derived from each dish's cuisine category cookProfileParameterName.
  const requiredCuisines = useMemo(() => {
    const map = new Map<string, string>();
    (trail?.dishes ?? []).forEach((d: any) => {
      const param = d?.dish?.category?.cookProfileParameterName;
      if (param) {
        map.set(param, d?.dish?.category?.name ?? cuisineLabelForParam(param));
      }
    });
    return Array.from(map.entries()).map(([param, label]) => ({ param, label }));
  }, [trail]);

  // Calculated Cook Cut is the same for every cook — shown once above the table.
  const calculatedCookCut = useMemo(() => {
    const row = data.find((r) => r?.calculatedCookCut != null);
    return row?.calculatedCookCut ?? null;
  }, [data]);

  // Customer location sector is identical for all cooks — derived from the party house area.
  const customerSector = trail?.areaName ?? trail?.areaId ?? "--";

  // Book-by-Dish parties also surface the cook's chefit (One-Meal-Service) rating & visits.
  const isBookByDish = trail?.bookingType === ChefForPartyBookingTypeEnum.BOOK_BY_DISH;

  // Cook ids the customer has blocked — flagged red and pushed to the bottom.
  const blockedCookIdSet = useMemo(
    () => new Set((blockedCooks ?? []).map((c: any) => Number(c?.cookId))),
    [blockedCooks]
  );

  // Cook id -> customer preference rank (lower rank = more preferred).
  const preferredRankByCookId = useMemo(() => {
    const map = new Map<number, number>();
    (preferredCooks ?? []).forEach((c: any) => {
      map.set(Number(c?.cookId), Number(c?.rank));
    });
    return map;
  }, [preferredCooks]);

  const sortedData = useMemo(() => {
    const availGroup = (r: any) => {
      const a = r?.availability;
      if (a === "AVAILABLE") return 0;
      if (a === "PARTIAL_AVAILABLE") return 1; // offerable, but clashing — below the fully free
      return 2;
    };
    const isMg = (r: any) => !!r?.cookFullProfile?.isMgCook;
    const ratingOf = (r: any) => Number(r?.cookCustomerRating) || 0;
    const distOf = (r: any) =>
      r?.distanceFromHouse != null
        ? Number(r.distanceFromHouse)
        : Number.POSITIVE_INFINITY;
    const fulfilledOf = (r: any) => Number(r?.fulfilledPartyVisitsCount) || 0;
    const acceptedRank = (r: any) =>
      r?.hasAcceptedParty || r?.leadAcceptedAt ? 0 : 1;
    // Customer-blocked cooks sink to the bottom.
    const blockedGroup = (r: any) =>
      blockedCookIdSet.has(Number(r?.cookId)) ? 1 : 0;
    // Customer-preferred cooks surface first, ordered by their rank.
    const preferredRank = (r: any) =>
      preferredRankByCookId.has(Number(r?.cookId))
        ? preferredRankByCookId.get(Number(r?.cookId))!
        : Number.POSITIVE_INFINITY;

    return data
      .filter(
        (row) =>
          row.cookFullProfile?.workStatus !== CookWorkStatus.BLACKLISTED &&
          row.cookFullProfile?.blackListed !== true && 
          // Only party chefs are eligible for party allocation.
          (trail.bookingType !== "BOOK_BY_DISH" ? isPartyChef(row.cookFullProfile) : true)
      )
      .sort((a, b) => {
        // Cooks who accepted the party lead always surface at the very top.
        const ac = acceptedRank(a) - acceptedRank(b);
        if (ac) return ac;
        // A. Availability — Available first
        const av = availGroup(a) - availGroup(b);
        if (av) return av;
        // Customer-blocked cooks always sink below non-blocked.
        const bl = blockedGroup(a) - blockedGroup(b);
        if (bl) return bl;
        // Customer-preferred cooks surface first (by rank).
        const pr = preferredRank(a) - preferredRank(b);
        if (pr) return pr;
        // B/C. Cook Type — MG before Part Time
        const tg = (isMg(a) ? 0 : 1) - (isMg(b) ? 0 : 1);
        if (tg) return tg;
        // Ratings (desc) -> Distance (asc) -> Fulfilled visits (desc)
        const rt = ratingOf(b) - ratingOf(a);
        if (rt) return rt;
        const ds = distOf(a) - distOf(b);
        if (ds) return ds;
        return fulfilledOf(b) - fulfilledOf(a);
      });
  }, [data, blockedCookIdSet, preferredRankByCookId, trail]);

  // ⭐ Controlled MRT state (prevents filter reset)
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([
    // Both offerable verdicts by default — a partially blocked cook is still assignable, so
    // hiding them would shrink the list ops works from.
    { id: "availability", value: ["AVAILABLE", "PARTIAL_AVAILABLE"] },
    {
      id: "workStatus",
      value: [CookWorkStatus.FREE_TO_WORK, CookWorkStatus.RE_TRAINING],
    },
    { id: "onboardingStatus", value: [FreshLeadStatus.ONBOARDING] },
    { id: "onboardingSubStatus", value: [AllCookOnBoardingSubStatus.COMPLETE] },
  ]);
  const [globalFilter, setGlobalFilter] = useState<string>("");
  // Business order is applied in `sortedData`; start with no column sort.
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    hasAcceptedParty: false,
    leadCityId: false, // Party City
    onboardingStatus: false, // OB Status
    onboardingSubStatus: false, // OB SubStatus
    workStatus: false, // Work Status
    cookSlot: false, // Cook Slot
    quotedCookCut: false, // Agreed Upon Cook Cut
  });


  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        id: "allocateCookAction",
        header: "Allocate",
        size: 95,
        enableColumnFilter: false,

        Cell: ({ row }) => {
          const availability = row.original.availability;

          // Show button ONLY when AVAILABLE or PARTIAL_AVAILABLE
          if (availability === "AVAILABLE" || availability === "PARTIAL_AVAILABLE") {
            return (
              <Button
                variant="contained"
                size="small"
                sx={compactBtnSx}
                onClick={() => {
                  setOpenAssignCook(true);
                  setAssignCookData({
                    id: row.original.cookId,
                    fname: row.original.cookFullProfile.firstName,
                    lname: row.original.cookFullProfile.lastName,
                  });
                }}
              >
                Allocate
              </Button>
            );
          }

          // For BLOCKED + ALLOCATED → no button, no chips (chips already in availability column)
          return null;
        },
      },
      {
        id: "availability",
        header: "Availability",
        size: 100,

        // Partial is its own verdict, not a shade of available: the cook has a clash of an hour
        // or less with the party window. Still offerable — the backend gate allows the same
        // tolerance — but ops needs to see it, so it never folds into AVAILABLE.
        accessorFn: (row) => row.availability,

        filterVariant: "multi-select",
        filterSelectOptions: [
          { text: "Available", value: "AVAILABLE" },
          { text: "Partially blocked", value: "PARTIAL_AVAILABLE" },
          { text: "Blocked", value: "BLOCKED" },
          { text: "Allocated", value: "ALLOCATED" },
        ],

        Cell: ({ row }) => {
          const a = row.original.availability;

          if (a === "AVAILABLE") {
            return (
              <Chip
                size="small"
                label="AVAILABLE"
                sx={{ backgroundColor: "blue", color: "white" }}
              />
            );
          }

          if (a === "PARTIAL_AVAILABLE") {
            return (
              <Chip
                size="small"
                label="PARTIAL"
                title="Clashes with another booking by up to an hour — the chef can still work this party"
                sx={{ backgroundColor: "#B45309", color: "white" }}
              />
            );
          }

          if (a === "BLOCKED") {
            return (
              <Chip
                size="small"
                label="BLOCKED"
                sx={{ backgroundColor: "red", color: "white" }}
              />
            );
          }

          if (a === "ALLOCATED") {
            return (
              <Chip
                size="small"
                label="ALLOCATED"
                sx={{ backgroundColor: "green", color: "white" }}
              />
            );
          }

          return "--";
        },
      },

      // Customer Preference — Blocked (never allocate) / Preferred (rank) by the customer
      {
        id: "customerPref",
        header: "Customer Pref",
        size: 110,
        accessorFn: (row) => {
          const cookId = Number(row.cookId);
          if (blockedCookIdSet.has(cookId)) return "Blocked";
          if (preferredRankByCookId.has(cookId)) return "Preferred";
          return "None";
        },
        filterVariant: "select",
        filterSelectOptions: [
          { text: "Preferred", value: "Preferred" },
          { text: "Blocked", value: "Blocked" },
          { text: "None", value: "None" },
        ],
        Cell: ({ row }) => {
          const cookId = Number(row.original.cookId);
          if (blockedCookIdSet.has(cookId)) {
            return (
              <Chip
                size="small"
                label="Blocked"
                sx={{ backgroundColor: "#991B1B", color: "white" }}
              />
            );
          }
          if (preferredRankByCookId.has(cookId)) {
            const rank = preferredRankByCookId.get(cookId);
            return (
              <Chip
                size="small"
                label={rank != null && !Number.isNaN(rank) ? `Preferred #${rank}` : "Preferred"}
                sx={{ backgroundColor: "#2e7d32", color: "white" }}
              />
            );
          }
          return <span>--</span>;
        },
      },

      // Acceptance — merged status + time (+ the "Update" action when undecided)
      {
        id: "acceptance",
        header: "Acceptance",
        size: 150,
        accessorFn: (row) => {
          if (row.leadAcceptedAt) return "Yes";
          if (row.leadRejectedAt) return "No";
          return "--";
        },
        filterVariant: "select",
        filterSelectOptions: [
          { text: "Yes", value: "Yes" },
          { text: "No", value: "No" },
        ],
        Cell: ({ row }) => {
          const { leadAcceptedAt, leadRejectedAt } = row.original;
          if (leadAcceptedAt) {
            return (
              <Box display="flex" flexDirection="column" gap={0.25}>
                <span style={{ backgroundColor: "#90ee8f", padding: 4, borderRadius: 6, textAlign: "center" }}>
                  Yes
                </span>
                <span style={{ fontSize: 11 }}>{formatDateTimeWithSeconds(leadAcceptedAt)}</span>
              </Box>
            );
          }
          if (leadRejectedAt) {
            return (
              <Box display="flex" flexDirection="column" gap={0.25}>
                <span style={{ backgroundColor: "#e53935", color: "white", padding: 4, borderRadius: 6, textAlign: "center" }}>
                  No
                </span>
                <span style={{ fontSize: 11 }}>{formatDateTimeWithSeconds(leadRejectedAt)}</span>
              </Box>
            );
          }
          return (
            <Button
              variant="contained"
              size="small"
              sx={compactBtnSx}
              onClick={() => {
                EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
                  message: "Mark Lead as Accepted",
                  onClose: (confirmed: boolean) => {
                    chefForPartyService
                      .acceptLeadForParty(row.original.id, confirmed)
                      .then(() => {
                        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                          message: "Marked Successfully",
                          type: CustomSnackbarTypes.SUCCESS,
                        });
                        stableReload();
                      });
                  },
                  title: "Confirmation",
                });
              }}
            >
              Update
            </Button>
          );
        },
      },

      // Chef — merged ID + Name + Mobile
      {
        id: "chef",
        header: "Chef",
        size: 160,
        enableHiding: false,
        accessorFn: (row) =>
          `${row.cookId ?? ""} ${row.cookFullProfile?.firstName ?? ""} ${
            row.cookFullProfile?.lastName ?? ""
          } ${row.cookPhoneNumber ?? ""}`,
        Cell: ({ row }) => (
          <Box display="flex" flexDirection="column" gap={0.25}>
            <Link href={`/#/Cooks/${row.original.cookId}/show/`} target="_self">
              #{row.original.cookId}
            </Link>
            <span>
              {`${row.original.cookFullProfile?.firstName ?? ""} ${
                row.original.cookFullProfile?.lastName ?? ""
              }`.trim() || "--"}
            </span>
            <CallButton
              toPhoneNumber={row.original.cookPhoneNumber}
              userType={UserType.COOK}
              referenceId={row.original.partyId}
              referenceModelName="CHEF_FOR_PARTY"
            />
          </Box>
        ),
      },

      // Cook Type — show MG / Part Time instead of Yes/No
      {
        id: "cookType",
        header: "Cook Type",
        size: 95,
        accessorFn: (row) => (row.cookFullProfile?.isMgCook ? "MG" : "Part Time"),
        filterVariant: "select",
        filterSelectOptions: [
          { text: "MG", value: "MG" },
          { text: "Part Time", value: "Part Time" },
        ],
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          return (
            <Chip
              label={value}
              size="small"
              sx={{
                backgroundColor: value === "MG" ? "#1565c0" : "#757575",
                color: "white",
              }}
            />
          );
        },
      },

      // Cuisines Known — cuisines whose per-cuisine rating is above threshold
      {
        id: "cuisinesKnown",
        header: "Cuisines Known",
        size: 140,
        enableColumnFilter: false,
        accessorFn: (row) =>
          getKnownCuisines(row.cookFullProfile)
            .map((c) => c.label)
            .join(", "),
        Cell: ({ row }) => {
          const known = getKnownCuisines(row.original.cookFullProfile);
          if (!known.length) return <span>--</span>;
          return (
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {known.map((c) => (
                <Chip key={c.param} label={c.label} size="small" />
              ))}
            </Box>
          );
        },
      },

      // Cuisine Rating — actual per-cuisine ratings for the party's required cuisines
      {
        id: "cuisineRating",
        header: "Cuisine Rating",
        size: 140,
        enableColumnFilter: false,
        accessorFn: (row) =>
          requiredCuisines
            .map((c) => `${c.label}: ${cuisineScore(row.cookFullProfile, c.param)}`)
            .join(", "),
        Cell: ({ row }) => {
          if (!requiredCuisines.length) return <span>--</span>;
          return (
            <Box display="flex" flexDirection="column" gap={0.25}>
              {requiredCuisines.map((c) => {
                const score = cuisineScore(row.original.cookFullProfile, c.param);
                return (
                  <span key={c.param}>
                    {c.label}:{" "}
                    <b style={{ color: score > CUISINE_SCORE_THRESHOLD ? "#2e7d32" : "#c62828" }}>
                      {score}
                    </b>
                  </span>
                );
              })}
            </Box>
          );
        },
      },

      // Selected Dish Cuisine Match — how many of the party's cuisines the cook knows
      {
        id: "cuisineMatch",
        header: "Dish Cuisine Match",
        size: 110,
        enableColumnFilter: false,
        accessorFn: (row) => {
          const total = requiredCuisines.length;
          if (!total) return -1;
          return requiredCuisines.filter(
            (c) => cuisineScore(row.cookFullProfile, c.param) > CUISINE_SCORE_THRESHOLD
          ).length;
        },
        Cell: ({ row }) => {
          const total = requiredCuisines.length;
          if (!total) return <span>--</span>;
          const matched = requiredCuisines.filter(
            (c) => cuisineScore(row.original.cookFullProfile, c.param) > CUISINE_SCORE_THRESHOLD
          ).length;
          const full = matched === total;
          return (
            <Chip
              label={`${matched}/${total}`}
              size="small"
              sx={{
                backgroundColor: full ? "#2e7d32" : matched > 0 ? "#ef6c00" : "#c62828",
                color: "white",
                fontWeight: 600,
              }}
            />
          );
        },
      },

      {
        accessorKey: "allocationNotes",
        header: "Allocation Comments",
        size: 220,
        filterFn: (row, col, filterValue) => {
          const notes = row.getValue(col);
          if (!Array.isArray(notes)) return false;
          return notes.some(
            (note) =>
              note.comment?.toLowerCase().includes(filterValue.toLowerCase()) ||
              note.commenterEmail
                ?.toLowerCase()
                .includes(filterValue.toLowerCase())
          );
        },
        Cell: ({ row }) => {
          const notes = row.original.allocationNotes;
          const addBtn = (
            <Button
              variant="contained"
              size="small"
              sx={compactBtnSx}
              onClick={() => {
                setOpenAddComment(true);
                setCommentData({
                  allocationId: row.original.id,
                  perviousComment: row.original.allocationNotes,
                });
              }}
            >
              Comment
            </Button>
          );
          // No comments yet → show the add button in the cell.
          // Has comments → show the table with the add button in its header.
          if (!notes?.length) return addBtn;
          return (
            <AllocationNotes
              notes={notes}
              headerAction={addBtn}
              currentPartyId={row.original.partyId}
            />
          );
        },
      },

      {
        id: "distanceFromHouse",
        header: "Distance (KM)",
        size: 95,
        accessorFn: (row) => row?.distanceFromHouse ?? null,
        filterFn: (row, id, filterValue) => {
          if (filterValue === "" || filterValue == null) return true;
          const maxDistance = parseFloat(filterValue);
          if (isNaN(maxDistance)) return true;
          const distance = row.getValue<number | null>(id);
          if (distance == null) return false;
          return distance <= maxDistance;
        },
        muiFilterTextFieldProps: {
          placeholder: "Max km",
          type: "number",
          inputProps: { min: 0 },
        },
        Cell: ({ row }) =>
          row.original.distanceFromHouse != null
            ? `${row.original.distanceFromHouse} km`
            : "--",
      },

      // Location Sector — merged Customer (party house area) -> Cook (hub area)
      {
        id: "locationSector",
        header: "Location Sector (Cust → Cook)",
        size: 150,
        enableColumnFilter: false,
        accessorFn: (row) => {
          const cook =
            row.cookFullProfile?.hubDetails?.areaName ??
            row.cookFullProfile?.hubDetails?.name ??
            "--";
          return `${customerSector} ${cook}`;
        },
        Cell: ({ row }) => {
          const cook =
            row.original.cookFullProfile?.hubDetails?.areaName ??
            row.original.cookFullProfile?.hubDetails?.name ??
            "--";
          return (
            <Box display="flex" flexDirection="column" gap={0.25}>
              <span><b>Cust:</b> {customerSector}</span>
              <span><b>Cook:</b> {cook}</span>
            </Box>
          );
        },
      },

      {
        id: "preferredCities",
        header: "Preferred Cities",
        size: 130,
        accessorFn: (row) => row.cookFullProfile?.preferredCities ?? [],
        Cell: ({ cell }) =>
          cell.getValue<string[]>().length
            ? cell.getValue<string[]>().join(", ")
            : "--",
        filterVariant: "multi-select",
        filterSelectOptions: cities.map((c) => ({
          text: c.name,
          value: c.name,
        })),
      },

      {
        accessorKey: "leadCityId",
        header: "Party City",
        size: 90,
        Cell: ({ row }) => (
          <Link
            href={`/#/Cities/${row.original.leadCityId}/show/`}
            target="_self"
          >
            {row.original.leadCityId ?? "--"}
          </Link>
        ),
      },

      {
        id: "partyRating",
        header: "Party Ratings",
        size: 90,
        accessorFn: (row) =>
          row?.cookCustomerRating
            ? Number(
              row?.cookCustomerRating
              ).toFixed(2)
            : "",
      },

      {
        id: "fulfilledPartyVisitsCount",
        header: "Fulfilled Visits",
        size: 95,
        accessorFn: (row) => row?.fulfilledPartyVisitsCount ?? 0,
      },

      // Chefit (One-Meal-Service) metrics — only relevant for Book-by-Dish parties.
      ...(isBookByDish
        ? [
            {
              id: "chefitRating",
              header: "Chefit Rating",
              size: 90,
              accessorFn: (row: any) =>
                row?.chefitRating ? Number(row.chefitRating).toFixed(2) : "",
            },
            {
              id: "fulfilledChefitVisitsCount",
              header: "Chefit Fulfilled Visits",
              size: 110,
              accessorFn: (row: any) => row?.fulfilledChefitVisitsCount ?? 0,
            },
          ]
        : []),

      // ⭐ Default-hidden columns (filters still apply when hidden)
      {
        id: "onboardingStatus",
        header: "OB Status",
        size: 130,
        accessorFn: (row) => row.cookFullProfile?.onboardingStatus ?? "",
        filterVariant: "multi-select",
        filterSelectOptions: CookOnBoardingTypes,
      },

      {
        id: "onboardingSubStatus",
        header: "OB SubStatus",
        size: 130,
        accessorFn: (row) => row.cookFullProfile?.onboardingSubStatus ?? "",
        filterVariant: "multi-select",
        filterSelectOptions: CookOnBoardingSubTypes,
      },
      {
        id: "workStatus",
        header: "Work Status",
        size: 130,
        accessorFn: (row) => row.cookFullProfile?.workStatus ?? "",
        filterVariant: "multi-select",
        filterSelectOptions: CookWorkStatusMap,
        filterFn: (row, id, selected) => {
          if (!selected?.length) return true;
          return selected.includes(row.getValue(id));
        },
      },

      {
        accessorKey: "cookSlot",
        header: "Cook Slot",
        size: 90,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Button
            variant="contained"
            size="small"
            sx={compactBtnSx}
            onClick={async () => {
              setShowSlots(true);
              const res = await partnerSlotService.getCookSlotsV2(
                row.original.cookId,
                trail?.date
              );
              setSlotData(res);
            }}
          >
            Slots
          </Button>
        ),
      },

      {
        id: "quotedCookCut",
        header: "Agreed Upon Cook Cut",
        size: 150,
        accessorFn: (row) => row.quotedCookCut ?? "",
        Cell: ({ row }) => (
          <Box display={'flex'} alignItems={'center'} gap={'0.5rem'} flexDirection={'column'}>
             <Box>{row?.original?.quotedCookCut ? `₹${Number(row?.original?.quotedCookCut)}` : '-'}</Box>
             <Button
                variant="contained"
                size="small"
                sx={compactBtnSx}
                onClick={() => {
                  setOpenPrice(true);
                  setPriceData({
                    partyId: row?.original?.partyId,
                    cookId: row?.original?.cookId,
                    previousPrice: row?.original?.quotedCookCut,
                  });
                }}
              >
                Update
              </Button>
          </Box>
        ),
      },

      {
        id: "chefNotes",
        header: "Chef Notes",
        size: 220,
        accessorFn: (row) => row.cookFullProfile?.chefNotes ?? [],
        filterFn: (row, col, filterValue) => {
          const notes = row.getValue(col);
          if (!Array.isArray(notes)) return false;
          return notes.some(
            (note: any) =>
              note.comment?.toLowerCase().includes(filterValue.toLowerCase()) ||
              note.commenterEmail?.toLowerCase().includes(filterValue.toLowerCase())
          );
        },
        Cell: ({ row }) => {
          const notes = row.original.cookFullProfile?.chefNotes;
          if (!notes?.length) return <div>--</div>;
          return <AllocationNotes notes={notes} />;
        },
      },

      {
        id: "sameDayVisitsCount",
        header: "Same Day Visits",
        size: 110,
        accessorFn: (row) => row?.sameDayVisitsCount ?? 0,
      },

      // Hidden helper column used for row highlight / ordering
      {
        id: "hasAcceptedParty",
        header: "hasAcceptedParty",
        accessorFn: (row) => (row.hasAcceptedParty ? 1 : 0),
        enableColumnFilter: false,
        enableHiding: false,
      },
    ],
    [trail, isBookByDish, cities, stableReload, requiredCuisines, customerSector, blockedCookIdSet, preferredRankByCookId]
  );

  return (
    <>
      {/* Calculated Cook Cut is identical for every cook — shown once above the filters */}
      <Box
        mb={2}
        p={1.5}
        sx={{
          backgroundColor: "#f0f4ff",
          border: "1px solid #c5cae9",
          borderRadius: 1,
          display: "inline-block",
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Calculated Cook Cut:{" "}
          {calculatedCookCut != null ? `₹${Number(calculatedCookCut)}` : "-"}
        </Typography>
      </Box>

      <MaterialReactTable
        columns={columns}
        data={sortedData}
        enableHiding
        enableColumnOrdering
        enableColumnActions={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableGlobalFilter
        muiTableHeadCellProps={headCellSx}
        muiTableBodyCellProps={bodyCellSx}
        // Shorten the long "Filter by {column}" placeholder so filter inputs stay narrow
        localization={{ filterByColumn: "Filter" }}

        // ⭐ Controlled state so filters/sorting NEVER reset
        state={{
          columnFilters,
          globalFilter,
          sorting,
          pagination,
          columnVisibility,
        }}
        onColumnFiltersChange={setColumnFilters}
        onGlobalFilterChange={setGlobalFilter}
        onSortingChange={setSorting}
        onPaginationChange={setPagination}
        onColumnVisibilityChange={setColumnVisibility}

        initialState={{
          density: "compact",
          showColumnFilters: true,
        }}
        muiTableBodyRowProps={({ row }) => {
          // Customer-blocked cooks are tinted red (takes priority over the accepted-green tint).
          if (blockedCookIdSet.has(Number(row.original.cookId))) {
            return {
              sx: {
                backgroundColor: "#fdecea",
                "& td": { backgroundColor: "#fdecea" },
              },
            };
          }
          return {
            sx: row.original.hasAcceptedParty ? {
              backgroundColor: "#e8f5e9",
              "& td": { backgroundColor: "#e8f5e9" },
            } : {},
          };
        }}
      />

      <ShowSlotsDialog
        showSlots={showSlots}
        setShowSlots={setShowSlots}
        data={slotData}
      />

      <AssignCookOnChefForParty
        data={trail}
        open={openAssignCook}
        setOpen={setOpenAssignCook}
        trialId={"1"}
        cook={assignCookData}
      />

      <AddPartyAllocationComment
        isOpen={openAddComment}
        setIsOpen={setOpenAddComment}
        data={commentData}
        setCommentData={setCommentData}
        refresh={() => {
          setOpenAddComment(false);
          stableReload();
        }}
      />
      <AddPartyQuotedPrice
        isOpen={openPrice}
        setIsOpen={setOpenPrice}
        data={priceData}
        setPriceData={setPriceData}
        refresh={() => {
          setOpenPrice(false);
          stableReload();
        }}
      />
    </>
  );
};

export default PartyAllocationMetaShow;
